/**
 * Wire contracts for the governed workflow-definition lifecycle API.
 *
 * Dates remain ISO-8601 strings at this boundary so callers can preserve the
 * server's exact values when reviewing effective windows and audit evidence.
 * ETags likewise remain opaque strings and must be returned unchanged in
 * conditional requests.
 */

export interface WorkflowDefinitionDocument {
  navigation?: WorkflowNavigationSettings | null;
  statusConfiguration?: WorkflowStatusConfiguration | null;
  payloadSchemaVersion: string;
  workflowCode: string;
  definitionVersionCode: string;
  subjectTypeCode: string | null;
  displayName?: string | null;
  description?: string | null;
  processData?: { fields: WorkflowProcessDataField[] } | null;
  scopeTypeCode: string;
  scopeKey: string | null;
  configurationCode: string;
  configurationVersionCode: string;
  instanceCardinalityCode: string;
  nodes: WorkflowDefinitionNode[];
  edges: WorkflowDefinitionEdge[];
  workflowTransitions: Array<WorkflowAllowedTransition | null> | null;
  requiredDecisions: Array<WorkflowDecisionRequirement | null> | null;
  assignmentPolicies?: Array<WorkflowAssignmentPolicy | null> | null;
  capabilityReferences?: WorkflowCapabilityReference[] | null;
  triggers?: WorkflowActionTrigger[] | null;
  numbering?: {
    mode: 'disabled' | 'required';
    policyId?: string | null;
    agencyCode?: string | null;
    programCode?: string | null;
  } | null;
  packReference?: {
    code: string;
    version: number;
    manifestHash: string;
  } | null;
}

export interface WorkflowNavigationSettings {
  iconKey: string;
  items: WorkflowNavigationItem[];
}

export interface WorkflowNavigationItem {
  nodeId: string;
  dashboardId: string;
  label?: string | null;
  iconKey?: string | null;
  sortOrder: number;
  visible: boolean;
}

export interface WorkflowAssignmentPolicy {
  /** Omission preserves the original queue-based assignment contract. */
  assignmentBasisCode?: 'roles' | null;
  id: string;
  scopeCode: WorkflowAssignmentPolicyScopeCode | string;
  nodeId: string | null;
  initialQueueCode: string;
  eligibleRoleCodes: Array<string | null> | null;
  eligibleQueueCodes: Array<string | null> | null;
  assignmentModeCode: WorkflowAssignmentModeCode | string;
  unassignedBehaviorCode: WorkflowAssignmentUnassignedBehaviorCode | string;
  fallbackBehaviorCode: WorkflowAssignmentFallbackBehaviorCode | string;
  returnBehaviorCode: WorkflowAssignmentReturnBehaviorCode | string;
  reentryBehaviorCode: WorkflowAssignmentReentryBehaviorCode | string;
}

export type WorkflowAssignmentPolicyScopeCode = 'workflow' | 'step';
export type WorkflowAssignmentModeCode =
  | 'queue_only'
  | 'claim'
  | 'direct'
  | 'claim_or_direct';
export type WorkflowAssignmentUnassignedBehaviorCode =
  | 'require_queue'
  | 'allow_unassigned';
export type WorkflowAssignmentFallbackBehaviorCode =
  | 'block'
  | 'initial_queue'
  | 'unassigned';
export type WorkflowAssignmentReturnBehaviorCode =
  | 'retain_assignment'
  | 'retain_queue'
  | 'initial_queue'
  | 'unassigned';
export type WorkflowAssignmentReentryBehaviorCode =
  | 'retain_assignment'
  | 'retain_queue'
  | 'initial_queue'
  | 'unassigned';

export interface WorkflowDefinitionNode {
  terminalOutcome?: WorkflowTerminalOutcome | null;
  /** Payload 1.15: a required system activity; never a staff task declaration. */
  automation?: WorkflowAutomatedStep | null;
  /** Omission retains immediate-start behavior in earlier publications. */
  stepProgressConfiguration?: WorkflowStepProgressConfiguration | null;
  statusConfiguration?: WorkflowStatusConfiguration | null;
  declaration?: WorkflowStepDeclaration | null;
  routing?: { conditionCode: string; conditionVersion: number } | null;
  id: string;
  typeCode: string;
  displayOrder: number;
  stepCode: string | null;
  displayName: string | null;
  allowedTransitions: Array<WorkflowAllowedTransition | null> | null;
  requiredDecisions: Array<WorkflowDecisionRequirement | null> | null;
}
export interface WorkflowAutomatedStep {
  version: 1;
  command: WorkflowCapabilityReference;
  inputs: WorkflowAutomatedInput[];
  outcomes: { commandOutcome: string; routeOutcome: string }[];
}

/** Web transport only; the automatic contract restores this exact JSON before canonical hashing. */
export type WorkflowAutomatedInput = WorkflowDeclaredAction['inputs'][number] & {
  literalJson?: string | null;
};

export interface WorkflowStepProgressConfiguration {
  startMode: 'assigned_open' | 'manual';
  bypass?: {
    conditionCode: string;
    conditionVersion: number;
    outcomeCode: string;
    reason: string;
    processDataKey?: string | null;
  } | null;
}

/** Saved array rows may be known Timer1, Timer2, future or opaque retained content. */
export type WorkflowSavedStepTimers = unknown[];

/** Versioned selections, never authorization grants or a second routing engine. */
export interface WorkflowStepDeclaration {
  behavior: {
    assignmentPolicyId: string | null;
    timers?: WorkflowSavedStepTimers | null;
    childLinks?: WorkflowStepChildLink[] | null;
    completionRequirements?:
      | import('@app/shared/workflow-commands/workflow-completion-requirements').WorkflowCompletionRequirement[]
      | null;
    commands: Array<{
      capability: WorkflowCapabilityReference;
      requirements: string[];
      outcomes: Array<{
        code: string;
        label: string;
        reasonPolicy: 'optional' | 'required';
        destinationNodeId: string;
      }>;
      captureForm?:
        | import('@app/shared/workflow-commands/workflow-capture-form').WorkflowCaptureForm
        | null;
      formConfiguration?:
        | import('@app/shared/workflow-commands/workflow-form-configuration').WorkflowFormConfiguration
        | null;
      inputBindings?: WorkflowProcessInputBinding[] | null;
      outputBindings?: WorkflowProcessOutputBinding[] | null;
    }>;
  };
  detail: {
    layout: 'workspace.two-column.v1';
    contextPlacement: 'side' | 'below' | 'hidden';
    tabs?: { id: string; label: string }[];
    formTabId?: string;
    blocks: Array<{
      instanceId: string;
      provider: string;
      code: string;
      version: number;
      settingsVersion: number;
      settings: Record<string, unknown>;
      region: 'main' | 'supporting';
      width: 'full' | 'half';
      tabId?: string;
    }>;
    mainInteraction?: {
      code: string;
      version: number;
      command: WorkflowCapabilityReference;
    } | null;
  };
  dashboards?: Array<{
    id: string;
    label: string;
    view: WorkflowCapabilityReference;
    scope: string;
    columns: string[];
    filterFields: string[];
    defaults: Record<string, unknown>;
    summaries: string[];
    quickActions: WorkflowCapabilityReference[];
    presentation?:
      | import('@app/workflow-dashboards/workflow-dashboard-presentation.models').DashboardPresentation
      | null;
    queueScope?: 'receiving-queue' | null;
    audienceRoleCodes?: string[] | null;
    /** Included work must match one of these authored process statuses. Unset includes every status. */
    workflowStatusCodes?: string[] | null;
    /** Included visits must match one of these authored statuses for this step. */
    stepStatusCodes?: string[] | null;
  }> | null;
}

export interface WorkflowChildDefinitionReference {
  workflowCode: string;
  definitionVersionCode: string;
  contentHash: string;
}
export interface WorkflowTerminalOutcome { version: number; code: string; label: string; }
export interface WorkflowStepChildLink {
  version: number;
  id: string;
  label: string;
  child: WorkflowChildDefinitionReference;
  mode: string;
  acceptedOutcomeCodes: string[];
  returnCode: string;
  cancelCode: string;
  failureCode: string;
  maximumDepth: number;
}

/** Explicit per-visit settings; installed service commands own warning/breach effects. */
export interface WorkflowStepTimer {
  version: number;
  id: string;
  label: string;
  startCode: string;
  durationUnit: string;
  durationAmount: number;
  calendar: { code: string; versionCode: string; contentHash: string } | null;
  dueLocalTime: string | null;
  gapCode: string | null;
  overlapCode: string | null;
  pauseCode: string;
  resetCode: string;
  reentryCode: string;
  warningLeadMinutes: number;
  warningAction: WorkflowTimerAction;
  breachAction: WorkflowTimerAction;
}

export interface WorkflowTimerAction extends Omit<WorkflowDeclaredAction, 'inputs'> {
  inputs: WorkflowAutomatedInput[];
}

export interface WorkflowProcessDataField {
  key: string;
  label: string;
  type:
    | 'string'
    | 'integer'
    | 'number'
    | 'boolean'
    | 'date'
    | 'record-reference';
  requiredAtStart?: boolean;
  recordTypeCode?: string | null;
}

export interface WorkflowProcessInputBinding {
  input: string;
  processDataKey: string;
  projection?: 'record-id' | null;
}

export interface WorkflowProcessOutputBinding {
  output: string;
  processDataKey: string;
}

export interface WorkflowDefinitionEdge {
  outcomeCode?: string | null;
  pathKindCode?: string | null;
  id: string;
  fromNodeId: string;
  toNodeId: string;
}

export interface WorkflowAllowedTransition {
  fromStateCode: string;
  toStateCode: string;
  requiredDecisionKinds: string[] | null;
  requiredDecisions: Array<WorkflowDecisionRequirement | null> | null;
  notifications?: Array<WorkflowNotificationRule | null> | null;
  notificationMessage?: WorkflowNotificationMessage | null;
}

/** Business progress labels; engine lifecycle and requirement evidence remain separate. */
export interface WorkflowStatusConfiguration {
  statuses: Array<{ code: string; label: string }>;
  initialStatusCode: string;
  allowManualChanges: boolean;
  rules: WorkflowStatusRule[];
}

export interface WorkflowStatusRule {
  id: string;
  eventCode: string;
  statusCode: string;
  requirementId?: string | null;
  sourceNodeId?: string | null;
  outcomeCode?: string | null;
}

export interface WorkflowDecisionRequirement {
  decisionKind: string;
  requiredOutcomeCode: string | null;
  reaction: WorkflowDecisionReaction | null;
  eligibleRoleCodes: Array<string | null> | null;
  ownerQueueCode: string | null;
  notifications?: Array<WorkflowNotificationRule | null> | null;
  notificationMessage?: WorkflowNotificationMessage | null;
}

/** Shared event content; approved context packets supply variables at rendering time. */
export interface WorkflowNotificationMessage {
  version: number;
  contextProfileCode: string;
  contextProfileVersion: number;
  titleTemplate: string;
  bodyTemplate: string;
}

export interface WorkflowNotificationVariable {
  code: string;
  label: string;
  description: string;
  sampleValue: string;
  eventKindCodes?: string[] | null;
}

export interface WorkflowNotificationContextProfile {
  code: string;
  version: number;
  label: string;
  description: string;
  subjectTypeCodes: string[];
  variables: WorkflowNotificationVariable[];
  eventKindCodes?: string[] | null;
}

export interface WorkflowNotificationRule {
  id: string;
  recipientTypeCode: WorkflowNotificationRecipientTypeCode | string;
  recipientTargetKey: string | null;
  severityCode: WorkflowNotificationSeverityCode | string;
}

export type WorkflowNotificationRecipientTypeCode =
  | 'current_assignee'
  | 'current_queue'
  | 'queue'
  | 'role'
  | 'manager'
  | 'external_destination';

export type WorkflowNotificationSeverityCode = 'info' | 'warning' | 'critical';

export interface WorkflowDecisionReaction {
  reactionCode: string;
  targetStateCode: string | null;
}

export interface WorkflowDefinitionDiagnostic {
  code: string;
  severityCode: string;
  message: string;
  locationTypeCode: string;
  locationId: string | null;
  path: string;
}

export interface WorkflowDefinitionAuthoringReferences {
  routingContracts?: Array<{
    code: string;
    version: number;
    outcomeCodes: string[];
    supportsStepBypass?: boolean;
    displayName?: string | null;
  }>;
  stateCodes: string[];
  decisionKindCodes: string[];
  decisionOutcomeCodes: string[];
  decisionReactionCodes: string[];
  subjectTypeCodes: string[];
  scopeTypeCodes: string[];
  configurationCodes: string[];
  configurationVersionKeys: string[];
  roleCodes: string[];
  queueCodes: string[];
  managerNotificationTargetCodes?: string[];
  externalNotificationTargetCodes?: string[];
  notificationContextProfiles?: WorkflowNotificationContextProfile[];
}

export interface WorkflowDefinitionDraftCreateCommand {
  workflowCode: string;
  draftCode: string;
  document: WorkflowDefinitionDocument;
}

export interface WorkflowDefinitionDraftCloneCommand {
  draftCode?: string;
}

export interface WorkflowDefinitionDraftUpdateCommand {
  document: WorkflowDefinitionDocument;
}

export interface WorkflowDefinitionDraftQuery {
  [key: string]: string | number | undefined;
  search?: string;
  workflowCode?: string;
  baseDefinitionVersionId?: number;
  validationStatusCode?: string;
  pageNumber: number;
  pageSize: number;
  includeArchived?: 'true' | 'false';
  includeHidden?: 'true' | 'false';
}

export type WorkflowDraftActionCode = 'create' | 'continue' | 'ambiguous';

/** Server-selected next-draft action. Missing metadata must not be guessed from cached drafts. */
export function workflowDraftActionLabel(
  action: WorkflowDraftActionCode | null | undefined,
): string {
  return action === 'continue'
    ? 'Continue draft'
    : action === 'ambiguous'
      ? 'Open drafts'
      : action === 'create'
        ? 'Create draft'
        : 'Checking draft…';
}

export interface WorkflowDraftLifecycle {
  /** Optional only for compatibility with earlier servers. Current servers always emit these fields. */
  lifecycleStatusCode?: 'open' | 'archived';
  archivedUtc?: string | null;
  publishedDefinitionVersionId?: number | null;
  publishedDefinitionVersionCode?: string | null;
  publishedRevision?: number | null;
}

export interface WorkflowDefinitionInheritedStepCode {
  nodeId: string;
  lineageNodeId: string;
  stepCode: string;
}

export interface WorkflowDefinitionDraftSummary extends WorkflowDraftLifecycle {
  isFamilyHidden?: boolean | null;
  id: number;
  workflowCode: string;
  draftCode: string;
  displayName?: string | null;
  baseDefinitionVersionId: number | null;
  baseDefinitionVersionCode?: string | null;
  revision: number;
  eTag: string;
  validationStatusCode: string;
  lastContentAuthorUserId: number | null;
  lastContentUpdatedUtc: string | null;
  lastValidatedByUserId: number | null;
  lastValidatedUtc: string | null;
  runtimeBindingCode: string;
  isExecutable: boolean;
}

export interface WorkflowDefinitionDraftPage {
  sharedVisibilityAvailable?: boolean;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  sortCode: string;
  asOfUtc: string;
  items: WorkflowDefinitionDraftSummary[];
}

export interface WorkflowDefinitionPublishCommand {
  displayName: string | null;
  reasonCode: string;
}

export interface WorkflowDefinitionActivationCommand {
  /** Null starts at the server's processing time. */
  effectiveStartUtc: string | null;
  effectiveEndUtc: string | null;
  reasonCode: string;
}

export interface WorkflowDefinitionRetireCommand {
  reasonCode: string;
}

export interface WorkflowDefinitionRollbackCommand {
  effectiveStartUtc: string | null;
  effectiveEndUtc: string | null;
  reasonCode: string;
}

export interface WorkflowDefinitionMakeCurrentCommand {
  expectedActivationId: number;
  expectedActivationETag: string;
  reasonCode: string;
}

export interface WorkflowDefinitionVersionChangePreview {
  definitionVersionId: number;
  actionCode:
    | 'activate'
    | 'make_current'
    | 'rollback'
    | 'current'
    | 'unavailable';
  currentActivationId: number | null;
  currentActivationETag: string | null;
  currentDefinitionVersionId: number | null;
  currentDefinitionVersionCode: string | null;
  currentDisplayName: string | null;
  effectiveEndUtc: string | null;
  asOfUtc: string;
}

export interface WorkflowDefinitionDraftDetail extends WorkflowDraftLifecycle {
  id: number;
  workflowCode: string;
  draftCode: string;
  baseDefinitionVersionId: number | null;
  revision: number;
  eTag: string;
  payloadSchemaVersion: string;
  compilerVersion: string;
  canonicalContentHash: string;
  validationStatusCode: string;
  validationDiagnostics: WorkflowDefinitionDiagnostic[];
  lastContentAuthorUserId: number | null;
  /** Omitted by legacy servers, which require a different publisher. */
  requiresIndependentPublisher?: boolean;
  lastContentUpdatedUtc: string | null;
  lastValidatedByUserId: number | null;
  lastValidatedUtc: string | null;
  runtimeBindingCode: string;
  isExecutable: boolean;
  inheritedStepCodes: WorkflowDefinitionInheritedStepCode[];
  document: WorkflowDefinitionDocument;
}

export interface WorkflowDefinitionDraftValidationResult {
  draftId: number;
  revision: number;
  eTag: string;
  validationStatusCode: string;
  compilerVersion: string;
  canonicalContentHash: string;
  runtimeBindingCode: string;
  isExecutable: boolean;
  diagnostics: WorkflowDefinitionDiagnostic[];
}

export interface WorkflowDefinitionDraftComparison {
  draftId: number;
  draftRevision: number;
  draftETag: string;
  definitionVersionId: number | null;
  baselineDefinitionVersionCode: string | null;
  draftContentHash: string;
  definitionContentHash: string | null;
  isIdentical: boolean;
  addedNodeIds: string[];
  removedNodeIds: string[];
  changedNodeIds: string[];
  changes: WorkflowDefinitionSemanticChange[];
}

export interface WorkflowDefinitionSemanticChange {
  changeTypeCode: string;
  elementTypeCode: string;
  elementId: string;
  path: string;
  summary: string;
  beforeValue: string | null;
  afterValue: string | null;
}

export interface WorkflowDefinitionLifecycleDetail {
  editableDraftId?: number | null;
  draftActionCode?: WorkflowDraftActionCode | null;
  id: number;
  workflowCode: string;
  definitionVersionCode: string;
  displayName: string | null;
  subjectTypeCode: string | null;
  configurationVersionId: number;
  configurationCode: string;
  configurationVersionCode: string;
  lifecycleStatusCode: string;
  activationPostureCode: string;
  effectiveEligibilityCode: string;
  publishedUtc: string | null;
  publishedByUserId: number | null;
  contentAuthorUserId: number | null;
  publicationReasonCode: string | null;
  payloadSchemaVersion: string | null;
  compilerVersion: string | null;
  canonicalContentHash: string | null;
  validationStatusCode: string;
  runtimeBindingCode: string;
  isExecutable: boolean;
  executabilityReasonCodes: string[];
  eTag: string | null;
  concurrencyModeCode: string;
  activationCount: number;
  totalInstanceCount: number;
  openInstanceCount: number;
  asOfUtc: string;
}

export interface WorkflowDefinitionActivationDetail {
  id: number;
  workflowDefinitionVersionId: number;
  workflowCode: string;
  definitionVersionCode: string;
  tenantKey: string;
  subjectTypeCode: string | null;
  scopeTypeCode: string;
  scopeKey: string | null;
  jurisdictionCode: string | null;
  programCode: string | null;
  configurationCode: string;
  configurationVersionId: number;
  configurationVersionCode: string;
  lifecycleStatusCode: string;
  activationPostureCode: string;
  effectiveEligibilityCode: string;
  effectiveStartUtc: string;
  effectiveEndUtc: string | null;
  activatedByUserId: number | null;
  activatedUtc: string | null;
  retiredByUserId: number | null;
  retiredUtc: string | null;
  reasonCode: string | null;
  revision: number;
  eTag: string;
  payloadSchemaVersion: string | null;
  compilerVersion: string | null;
  canonicalContentHash: string | null;
  validationStatusCode: string;
  runtimeBindingCode: string;
  isExecutable: boolean;
  executabilityReasonCodes: string[];
  concurrencyModeCode: string;
  asOfUtc: string;
}

export interface WorkflowDefinitionUsageImpact {
  definitionVersionId: number;
  workflowCode: string;
  definitionVersionCode: string;
  lifecycleStatusCode: string;
  activationPostureCode: string;
  effectiveEligibilityCode: string;
  payloadSchemaVersion: string | null;
  compilerVersion: string | null;
  canonicalContentHash: string | null;
  runtimeBindingCode: string;
  validationStatusCode: string;
  isExecutable: boolean;
  executabilityReasonCodes: string[];
  eTag: string | null;
  concurrencyModeCode: string;
  totalInstanceCount: number;
  openInstanceCount: number;
  completedInstanceCount: number;
  scheduledActivationCount: number;
  activeActivationCount: number;
  expiredActivationCount: number;
  retiredActivationCount: number;
  earliestInstanceUtc: string | null;
  latestInstanceUtc: string | null;
  asOfUtc: string;
}

export interface WorkflowDefinitionActivationQuery {
  [key: string]: number | boolean;
  pageNumber: number;
  pageSize: number;
  includeRetired: boolean;
}

export interface WorkflowDefinitionActivationPage {
  definitionVersionId: number;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  sortCode: string;
  asOfUtc: string;
  items: WorkflowDefinitionActivationDetail[];
}

export interface WorkflowDefinitionCatalogQuery {
  [key: string]: string | number | undefined;
  search?: string;
  workflowCode?: string;
  lifecycleStatusCode?: string;
  activationPostureCode?: string;
  includeHidden?: 'true' | 'false';
  pageNumber: number;
  pageSize: number;
}

export interface WorkflowDefinitionFamilyQuery {
  [key: string]: string | number | boolean | undefined;
  includeHidden?: boolean;
  search?: string;
  workflowCode?: string;
  pageNumber: number;
  pageSize: number;
}

/** Counts cover the entire workflow family, before family paging. */
export interface WorkflowDefinitionFamilySummary {
  visibility?: WorkflowDefinitionFamilyVisibility | null;
  workflowCode: string;
  displayName: string | null;
  retainedVersionCount: number;
  publishedVersionCount: number;
  openDraftCount: number;
  archivedDraftCount: number;
  latestPublishedDefinitionVersionId: number | null;
  latestPublishedDefinitionVersionCode: string | null;
  latestPublishedUtc: string | null;
}

export interface WorkflowDefinitionFamilyPage {
  sharedVisibilityAvailable?: boolean;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  sortCode: string;
  asOfUtc: string;
  items: WorkflowDefinitionFamilySummary[];
}

export interface WorkflowDefinitionFamilyVisibility {
  workflowCode: string;
  isAvailable: boolean;
  isHidden: boolean | null;
  revision: number | null;
  eTag: string | null;
  updatedUtc: string | null;
  updatedByUserId: number | null;
}

export interface WorkflowDefinitionFamilyVisibilityCommand {
  isHidden: boolean;
  expectedRevision: number;
}

export interface WorkflowDefinitionCatalogPage {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  sortCode: string;
  asOfUtc: string;
  items: WorkflowDefinitionLifecycleDetail[];
}

/** Exact installed contracts retained in a payload 1.4 publication. */
export interface WorkflowCapabilityReference {
  key: { provider: string; kind: string; code: string; version: number };
  inputSchemaHash: string;
  outputSchemaHash: string;
  contractHash: string;
}

/** Editing one feature must never downgrade another feature's document contract. */
export function requireWorkflowPayloadVersion(
  current: string,
  minimum: string,
): string {
  const versions = [
    '1.0',
    '1.1',
    '1.2',
    '1.3',
    '1.4',
    '1.5',
    '1.6',
    '1.7',
    '1.8',
    '1.9',
    '1.10',
    '1.11',
    '1.12',
    '1.13',
    '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'];
  const index = versions.indexOf(current);
  return index >= 0 && index < versions.indexOf(minimum) ? minimum : current;
}

/** Version 1.5 declarations; execution requires installed trigger dispatch. */
export interface WorkflowActionTrigger {
  id: string;
  kind: string;
  nodeId: string;
  edgeId?: string | null;
  outcomeCode?: string | null;
  actions: WorkflowDeclaredAction[];
}
export interface WorkflowDeclaredAction {
  id: string;
  typeCode: string;
  command: WorkflowCapabilityReference;
  inputs: {
    field: string;
    source: string;
    value?: unknown;
    context?: string | null;
  }[];
  outcomes: string[];
  group: string;
  order: number;
  mode: string;
  failurePolicy: string;
  caller: string;
  requiredForProgression: boolean;
}
